import { useStores } from '@littlebird/sdk';

import {
  refreshRoutineReads,
  routineReadState,
  useRoutineRefresh,
} from '@/features/routines/use-routine-refresh';
import { useProactiveAgentSelection } from '@/features/settings/proactive-agent-selection';
import {
  useInternalProactiveAgentView,
  useProactiveAgentsQuery,
} from '@/features/settings/proactive-agent.query';
import { settingsStore } from '@/stores/settings.store';

import {
  proactiveAgentCardFilter,
  proactiveAgentLabel,
} from './proactive-agent-visibility';

export const useProactiveAgentFeed = () => {
  // React Compiler would cache the MobX store reads below and miss their changes
  'use no memo';
  const agents = useProactiveAgentsQuery();
  const { reports, routines } = useStores();
  const {
    instances,
    names,
    selectedAgent,
    selectedAgents,
    homeFiltersByAgent: enabled,
  } = useProactiveAgentSelection(agents.data);
  const setEnabled = useInternalProactiveAgentView(instances);
  const hasAgents = names.length > 0;
  // The home switch Hide your own cards, stored under its old name. It applies only while an agent
  // is selected
  const hidesCardsOfNoAgent =
    selectedAgent !== 'none' && settingsStore.onlySelectedProactiveAgent;
  useRoutineRefresh(enabled && hasAgents);

  return {
    enabled,
    setEnabled,
    isPending: enabled && agents.isPending,
    sourcesPending:
      hasAgents &&
      (!routineReadState.hasLoadedReports ||
        !routineReadState.hasLoadedRoutines),
    isError: enabled && agents.isError,
    retry: () => {
      void agents.refetch();
      refreshRoutineReads();
    },
    hasAgents,
    // The user's default agent, set on the server for all devices (see `ProactiveAgents`)
    defaultAgent: agents.data?.default_agent,
    selectedAgent,
    selectedLabel: selectedAgents.length
      ? selectedAgents.join(', ')
      : proactiveAgentLabel('none'),
    hidesCardsOfNoAgent,
    setHidesCardsOfNoAgent: (value: boolean) =>
      settingsStore.setOnlySelectedProactiveAgent(value),
    // Looks up each card's owner in the stores when the card is checked, so a changed report or
    // routine takes effect on the next render
    acceptsCard: proactiveAgentCardFilter(
      selectedAgents,
      instances,
      {
        reportByChatId: (chatId) => reports.getByChatId(chatId),
        routineByReferenceId: (referenceId) =>
          routines.getByReferenceId(referenceId),
      },
      hidesCardsOfNoAgent
    ),
  };
};
